// Common link helper of CRM Postventa (docs/crm-postventa-navegacion/README.md §7). Links between CRM screens carry
// their context in the URL so they can be opened in another tab, shared and left with "Atrás":
//   ?cliente=<IdAlta> for the alta (one terminal of a comercio), &gestion=<id> for a gestión, &editar=1 for the Ficha
//   editor, &nueva=ipc|proactiva (&tipo=modificacion|reclamo|consulta with ipc, &terminal=<terminal number>) for the
//   alta of Gestiones IPC. Altas/Bajas also receives ?cliente=, although it does not read it yet.
// The alta id (IdAlta) is always called clienteId and the terminal number numeroTerminal; only the &terminal= query
// name carries the number. Screens never build these URLs by hand.

export const CRM_PATHS = {
  ficha: "/administracion/crm-postventa/ficha",
  gestion: "/administracion/crm-postventa/gestion",
  altasBajas: "/administracion/crm-postventa/altas-bajas",
};

export const CLIENTE_PARAM = "cliente";
const GESTION_PARAM = "gestion";
const EDITAR_PARAM = "editar";
const NUEVA_PARAM = "nueva";
const TIPO_PARAM = "tipo";
const TERMINAL_PARAM = "terminal";
const NUEVA_VALUES = { IPC: "ipc", PROACTIVA: "proactiva" };
// IPC type codes and their &tipo= values (only with nueva=ipc: a Proactiva has no type).
const TIPO_VALUES = { MODIFICACION: "modificacion", RECLAMO: "reclamo", CONSULTA: "consulta" };

// Active comercio (IdAlta) that the CRM screens keep in the browser: the last one chosen in the Ficha or another
// screen of the client.
export const ACTIVE_CLIENTE_STORAGE_KEY = "crm_postventa_cliente_id";

const WHOLE_NUMBER = /^\d+$/;

// A positive whole id (number or numeric text), or null.
const positiveId = (value) => {
  const text = String(value ?? "").trim();
  if (!WHOLE_NUMBER.test(text)) return null;
  const id = Number(text);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

// A terminal number (digits only, as the server stores it), or null.
const toNumeroTerminal = (value) => {
  const text = String(value ?? "").trim();
  return WHOLE_NUMBER.test(text) ? text : null;
};

const buildHref = (path, params) => {
  const query = params
    .filter(([, value]) => value !== null && value !== undefined && value !== "")
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join("&");
  return query ? `${path}?${query}` : path;
};

// Ficha of an alta (clienteId = IdAlta). gestionId and editar are for "Editar datos" (the Ficha opens its editor and
// offers "Volver a la gestión"). Without a valid alta there is no link: null.
export const fichaHref = ({ clienteId, gestionId = null, editar = false } = {}) => {
  const cliente = positiveId(clienteId);
  if (!cliente) return null;
  return buildHref(CRM_PATHS.ficha, [
    [CLIENTE_PARAM, cliente],
    [GESTION_PARAM, positiveId(gestionId)],
    [EDITAR_PARAM, editar ? "1" : null],
  ]);
};

// { clienteId, gestionId, editar } of a Ficha link ("Editar datos" of a gestión, T5): the ids are null when missing or
// invalid, and editar is true only for &editar=1.
export const readFichaEditParams = (search) => {
  const params = new URLSearchParams(String(search ?? ""));
  return {
    clienteId: positiveId(params.get(CLIENTE_PARAM)),
    gestionId: positiveId(params.get(GESTION_PARAM)),
    editar: params.get(EDITAR_PARAM) === "1",
  };
};

// &gestion= of a query string, or null when it is missing or not a positive whole number.
export const readGestionIdParam = (search) => positiveId(new URLSearchParams(String(search ?? "")).get(GESTION_PARAM));

// ?cliente= of a query string (location.search), or null when it is missing or not a positive whole number.
export const readClienteIdParam = (search) => {
  const params = new URLSearchParams(String(search ?? ""));
  return positiveId(params.get(CLIENTE_PARAM));
};

// Gestión screen, with the alta (clienteId = IdAlta) when it is known, and the gestión whose Gestionar the queue opens
// ("Volver a la gestión" from the Ficha).
export const gestionHref = ({ clienteId = null, gestionId = null } = {}) =>
  buildHref(CRM_PATHS.gestion, [
    [CLIENTE_PARAM, positiveId(clienteId)],
    [GESTION_PARAM, positiveId(gestionId)],
  ]);

// Altas/Bajas ("Ir a Altas/Bajas" of a Modificación, N8). It carries ?cliente= by the common rule, but the screen does
// not read it yet: the asesor searches the comercio there.
export const altasBajasHref = ({ clienteId = null } = {}) => buildHref(CRM_PATHS.altasBajas, [[CLIENTE_PARAM, positiveId(clienteId)]]);

// IPC type code ("RECLAMO") of a type code or &tipo= value, or null when it is not one.
const toTipo = (value) => {
  const text = String(value ?? "").trim().toLowerCase();
  return Object.keys(TIPO_VALUES).find((key) => TIPO_VALUES[key] === text) || null;
};

// Alta of Gestiones IPC ("Registrar gestión" in the Ficha, Prompt 7): the alta (clienteId = IdAlta) of the comercio,
// the category, the IPC type (only for ipc) and the terminal number, so the alta opens in step 2 with them loaded.
// Without a valid alta or category there is no link: null.
export const nuevaGestionHref = ({ categoria, clienteId, tipo = null, numeroTerminal = null } = {}) => {
  const cliente = positiveId(clienteId);
  const nueva = NUEVA_VALUES[String(categoria ?? "").trim().toUpperCase()];
  if (!cliente || !nueva) return null;
  const tipoCode = nueva === NUEVA_VALUES.IPC ? toTipo(tipo) : null;
  return buildHref(CRM_PATHS.gestion, [
    [CLIENTE_PARAM, cliente],
    [NUEVA_PARAM, nueva],
    [TIPO_PARAM, tipoCode ? TIPO_VALUES[tipoCode] : null],
    [TERMINAL_PARAM, toNumeroTerminal(numeroTerminal)],
  ]);
};

// { categoria: "IPC" | "PROACTIVA", clienteId, numeroTerminal, tipo } of an alta link, or null when it is not one.
// tipo is the IPC type code of &tipo= ("RECLAMO"), null when it is missing, unknown or the alta is a Proactiva.
export const readNuevaGestionParams = (search) => {
  const params = new URLSearchParams(String(search ?? ""));
  const clienteId = positiveId(params.get(CLIENTE_PARAM));
  const nueva = String(params.get(NUEVA_PARAM) ?? "").trim().toLowerCase();
  const categoria = Object.keys(NUEVA_VALUES).find((key) => NUEVA_VALUES[key] === nueva);
  if (!clienteId || !categoria) return null;
  return {
    categoria,
    clienteId,
    numeroTerminal: toNumeroTerminal(params.get(TERMINAL_PARAM)),
    tipo: nueva === NUEVA_VALUES.IPC ? toTipo(params.get(TIPO_PARAM)) : null,
  };
};

// The stored active comercio, or null when there is none or the browser storage is not available.
export const readActiveClienteId = () => {
  try {
    return positiveId(window.localStorage.getItem(ACTIVE_CLIENTE_STORAGE_KEY));
  } catch (storageError) {
    return null;
  }
};

// A plain left click navigates inside the app; a click with a modifier key keeps the browser default (new tab).
export const isPlainClick = (event) =>
  Boolean(event) && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
